'use client';

import { createContext, use, useLayoutEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useParams } from 'next/navigation';

import type { InitOptions } from 'i18next';

import { I18nProvider } from '@kit/i18n/provider';

import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { normalizeTranslationBusinessType } from '~/lib/i18n/business-type';

type AccountVocabulary = { account: string; businessType: string | null };

const AccountVocabularyContext = createContext<{
  businessType: string | null;
  setVocabulary: Dispatch<SetStateAction<AccountVocabulary>>;
} | null>(null);

export function TenantI18nProvider({
  businessType = null,
  customerBusinessType = null,
  settings,
  children,
}: React.PropsWithChildren<{
  businessType?: string | null;
  customerBusinessType?: string | null;
  settings: InitOptions;
}>) {
  const params = useParams<{ account?: string }>();
  const account = params?.account;
  const [vocabulary, setVocabulary] = useState<AccountVocabulary>(() => ({
    account: account ?? '',
    businessType: normalizeTranslationBusinessType(businessType),
  }));
  const effectiveBusinessType = account && vocabulary.account === account
    ? vocabulary.businessType
    : normalizeTranslationBusinessType(customerBusinessType);
  const context = useMemo(() => ({
    businessType: effectiveBusinessType,
    setVocabulary,
  }), [effectiveBusinessType]);

  return (
    <AccountVocabularyContext.Provider value={context}>
      <I18nProvider
        settings={settings}
        instanceKey={effectiveBusinessType ?? ''}
        resolver={((language: string, namespace: string) =>
          i18nResolver(language, namespace, effectiveBusinessType)) as (
            language: string,
            namespace: string,
          ) => Promise<Record<string, string>>}
      >
        {children}
      </I18nProvider>
    </AccountVocabularyContext.Provider>
  );
}

export function AccountI18nScope({
  account,
  businessType: rawBusinessType,
  children,
}: React.PropsWithChildren<AccountVocabulary>) {
  const context = use(AccountVocabularyContext);
  const businessType = normalizeTranslationBusinessType(rawBusinessType);

  if (!context) {
    throw new Error('AccountI18nScope requires TenantI18nProvider');
  }

  const { setVocabulary } = context;

  useLayoutEffect(() => {
    setVocabulary((current) =>
      current.account === account && current.businessType === businessType
        ? current
        : { account, businessType },
    );
  }, [account, businessType, setVocabulary]);

  return context.businessType === businessType ? children : null;
}
